import { routes } from "@/global/routes";
import { vendorUUIDs } from "../../constants";
import { howItWorks, faqs } from "./helpers";
import {
  DefaultCheckoutTerms,
  DefaultRegistrationTerms,
} from "../../default_terms_and_conditions";

import AuthBgBanner from "public/images/devils/devils.png";
import FindGamesBanner from "public/images/devils/7GamePickEmPlan.png";
import SeatMap3dPreview from "public/images/devils/devils_flex_3d_venue.png";

import type { TeamConfig } from "@/global/hooks/useTeamConfig";

const logoImage =
  "https://ss-leagues.s3.amazonaws.com/NHL_2024/NewJersey-Devils-2024.jpg";

const config: TeamConfig = {
  teamSchema: "devils_miniplan",
  browserTitle: "New Jersey Devils Pick 'Em Plan",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  teamName: "New Jersey Devils",

  packs: {
    enabled: true,
    list: [
      {
        id: 1,
        priritizePackAmountForEventCount: true,
        amount: 7,
        title: "7 Game Pack",
        description:
          "Choose 7 games and create the ultimate flexible game pack, perfectly tailored to your schedule.",
        offerMoreMessage:
          "Add 2 more games and unlock the opportunity to purchase one available Rangers game!",
        buckets: [
          // {
          //   id: 1,
          //   title: "Rangers Game",
          //   description: "Pick up to one Rangers Game!",
          //   max: 1,
          //   min: 0,
          //   disabled: true,
          //   opened: false,
          // },
          {
            id: 2,
            title: "Regular Season Games",
            description: "",
            max: 9,
            min: 7,
            disabled: false,
            opened: true,
            dontCloseWhenHitMin: true,
          },
        ],
        updatedAccountGroup: 0,
        successMessage: "Add more games or checkout now!",
      },
      {
        id: 2,
        priritizePackAmountForEventCount: true,
        scrollToTopWhenSelecting: true,
        amount: 10,
        title: "Rangers Game Pack",
        description:
          "Choose 10 games, including 1 Rangers game creating the ultimate flexible game pack.",
        offerMoreMessage: "",
        defaultSelected: true,
        buckets: [
          {
            id: 1,
            title: "Rangers Game",
            description: "Pick One",
            max: 1,
            min: 0,
            disabled: false,
            opened: true,
          },
          {
            id: 2,
            title: "Regular Season Games",
            description: "",
            max: 999,
            min: 9,
            disabled: false,
            opened: true,
            dontCloseWhenHitMin: true,
          },
        ],
        successMessage: "Add more games or checkout now!",
        updatedAccountGroup: 0,
      },
    ],
  },

  manageCard: true,

  // seat map configs
  seatMapConfigs: {
    venueId: "nam-us-10215-devils-sec",
    mapId: "main",
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.purchaseSeats,
  },

  // disabled routes
  disabledRoutes: [routes.giftCard],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: true,
  inventoryType: "SEAT-LEVEL",

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 7,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: true,
    allowManualCartInSimilar: false,
    isCheckoutEditSeatsEnabled: false,
    isDiffSeatAlertEnabled: false,
    checkoutTimerEnabled: false,
    showFrontendTotalAmount: false,
    showFrontendServiceFee: false,

    hideViewTicketsInThankYouPage: false,
    defaultSeatCount: 2,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
    checkoutTimerEnabled: false,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },
  isSaveCardAllowed: true,

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "G-XT9NZ1847J",
    attrkPixelId: "",
    beaconId: "",
    metaPixelId: "120085088485875",
    stackAdaptPixelId: "pmYksymwpGkOLia4azzaJQ",
  },

  // call refresh inventory when selecting games
  refreshInventoryWhenEventClick: false,

  shouldCheckZipCode: false,

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  // urls
  accountManagerUrl: "https://am.ticketmaster.com/devils/",

  // vendor
  platform: vendorUUIDs.ticketmaster,

  // okta config
  oktaIssuer: "",
  oktaClientId: "",

  // images
  images: {
    findGamesBannerImage: FindGamesBanner.src,
    authBackgroundImage: AuthBgBanner.src,
    checkoutStatusPageImage: AuthBgBanner.src,
    logoImage: `${logoImage}?width=64&height=64`,
    logoNameImage: "",
    faviconImage:
      "https://ss-leagues.s3.amazonaws.com/NHL/NewJerseyDevils-aug21.png",
    seatMap3dPreviewImage: SeatMap3dPreview.src,
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: false,
    },
    bottomBar: {
      showGameSelectionProgress: true,
    },

    hideCheckoutCartBalance: true,
  },

  colors: {
    primary: "#CE1126",
  },

  howItWorks,
  faqs,

  languages: {
    topNavMenuItems: "",
    checkoutTermsAndCondition: <DefaultCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,
    purchaseSeats: null,
    purchaseGiftCard: null,
    redeemGiftCard: null,

    checkoutSuccessMessage:
      "Tickets are available in your New Jersey Devils Account Manager!",

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },
    signInAuthText:
      "Connect your Ticketmaster account to continue with seamless access.",
  },
};

export default config;
